import React, { useEffect, useMemo, useRef, useState } from 'react';

import { LENS_MODAL_HEADER, lensTokens } from '~/configs/unifiedAnalyticsTheme';

import CloseIcon from '@mui/icons-material/Close';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { FONT_STACK_SUISSE_INTL } from '~/components/brand';

import { useGetReportingLeLens } from '~/hooks/apis/reporting/useGetReportingLeLens';
import { useActOnLensCertification } from '~/hooks/apis/reporting/useLensCertification';
import { useUpdateLensSharing } from '~/hooks/apis/reporting/useLensSharing';

import type { Lens, LensResponse, LensRole, LensShareRecipient, LensSpace } from '~/types/leLens';

import LensPublishCopyButton from './LensPublishCopyButton';
import LensRecipientInput from './LensRecipientInput';
import LensShareExport from './LensShareExport';
import type { LensState } from './document/types';
import { lensShareUrl } from './lensShareContent';
import type { LensUrlState } from './lensUrlState';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Direct shares grant one of two things. Manager is held by the owner and by a space's managers. */
const RECIPIENT_ROLES: Array<{ value: LensRole; label: string }> = [
  { value: 'viewer', label: 'Can view' },
  { value: 'editor', label: 'Can edit' },
];

interface ShareLensDialogProps {
  open: boolean;
  onClose: () => void;
  lens: Lens | null;
  /** The spaces the signed-in employee belongs to, which are the only homes they can move a Lens into. */
  spaces: Array<LensSpace>;
  viewerEmail?: string | null;
  currentView?: LensUrlState;
  documentState?: LensState;
  spacesLoading?: boolean;
  spacesError?: boolean;
  publishingDisabled?: boolean;
}

/**
 * One dialog for the whole question of who can see a Lens, disclosed in the order
 * a person decides it: what is true now, then the lightweight one-off share, then
 * a durable team home.
 */
export default function ShareLensDialog({
  open,
  onClose,
  lens,
  spaces,
  viewerEmail,
  currentView,
  documentState,
  spacesLoading,
  spacesError,
  publishingDisabled = false,
}: ShareLensDialogProps) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const updateSharing = useUpdateLensSharing();
  const certificationAction = useActOnLensCertification();
  const [certificationError, setCertificationError] = useState<string | null>(null);
  const [includeView, setIncludeView] = useState(true);
  const [moveError, setMoveError] = useState<string | null>(null);
  const writableSpaces = spaces.filter(
    (space) => !space.archivedAt && (space.role === 'manager' || (space.isMember ?? Boolean(space.role))),
  );
  /**
   * The recipient list lives on the detail response, not the list one: naming the
   * colleagues a Lens was shared with is only shown to someone who may change it,
   * so it is not carried on every row of a library page.
   */
  const detail = useGetReportingLeLens(open && lens ? lens.id : undefined);
  const recipients = useMemo(() => detail.data?.sharing?.recipients ?? [], [detail.data]);
  const recipientsLoaded = !detail.isLoading && !detail.isError;

  const selectedLensRef = useRef(lens);
  selectedLensRef.current = lens;
  const hydratedLensId = useRef<string | null>(null);
  const [activeLens, setActiveLens] = useState<Lens | null>(null);
  const [draft, setDraft] = useState<Array<LensShareRecipient>>([]);
  const [confirmedRecipients, setConfirmedRecipients] = useState<Array<LensShareRecipient>>([]);
  const [invitee, setInvitee] = useState('');
  const [inviteeRole, setInviteeRole] = useState<LensRole>('viewer');
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [sharingFeedback, setSharingFeedback] = useState<{ message: string; severity: 'error' | 'success' } | null>(
    null,
  );
  const [moveTo, setMoveTo] = useState<string>('');
  const [newManager, setNewManager] = useState('');
  const [copied, setCopied] = useState(false);
  const [copying, setCopying] = useState(false);
  const [published, setPublished] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);
  const [linkAccessEnabled, setLinkAccessEnabled] = useState(false);
  const [confirmedLinkAccessEnabled, setConfirmedLinkAccessEnabled] = useState(false);
  const [moreOptions, setMoreOptions] = useState(false);
  const [withdrawingCompany, setWithdrawingCompany] = useState(false);
  const [companyError, setCompanyError] = useState<string | null>(null);

  const currentLens = activeLens?.id === lens?.id ? activeLens : lens;
  const home = currentLens?.home;
  const currentSpaceId = home?.kind === 'space' ? home.spaceId : '';
  const personalHome = !currentSpaceId;
  const currentTeam = spaces.find((space) => space.id === currentSpaceId);
  const linkAccessSupported = typeof detail.data?.sharing?.linkAccess?.enabled === 'boolean';
  const canShare = !detail.isError && Boolean(detail.data?.access?.canShare ?? currentLens?.access?.canShare);
  const effectiveAccess = detail.data?.access ?? currentLens?.access;
  const canShareEdit = Boolean(effectiveAccess?.canShareEdit ?? effectiveAccess?.canEdit);
  const canManageSharing = Boolean(effectiveAccess?.canManageSharing ?? effectiveAccess?.role === 'manager');
  const canChangeLinkAccess = canManageSharing && (personalHome || Boolean(currentTeam && !currentTeam.archivedAt));
  const canMoveHome =
    canManageSharing || (personalHome && currentLens?.ownerEmail.toLowerCase() === viewerEmail?.toLowerCase());
  const certified = currentLens?.certification?.badge.kind === 'certified';
  const shareCount = confirmedRecipients.length;
  const shareLimitReached = shareCount >= 10;
  const allowedRecipientRoles = RECIPIENT_ROLES.filter((role) => canShareEdit || role.value === 'viewer');
  const busy = copying || updateSharing.isPending || certificationAction.isPending || detail.isLoading;

  useEffect(() => {
    const selectedLens = selectedLensRef.current;
    if (open && selectedLens) {
      hydratedLensId.current = null;
      setActiveLens(selectedLens);
      setDraft([]);
      setConfirmedRecipients([]);
      setInvitee('');
      setInviteeRole('viewer');
      setInviteError(null);
      setSharingFeedback(null);
      setMoveTo(selectedLens.home?.kind === 'space' ? selectedLens.home.spaceId : '');
      setNewManager(viewerEmail ?? '');
      setMoveError(null);
      setCopied(false);
      setCopying(false);
      setPublished(false);
      setCopyError(null);
      setLinkAccessEnabled(false);
      setConfirmedLinkAccessEnabled(false);
      setIncludeView(true);
      setCertificationError(null);
      setMoreOptions(false);
      setWithdrawingCompany(false);
    }
  }, [lens?.id, open, viewerEmail]);

  // Hydrate the editable draft once per opening. A refetch after a mutation must
  // not overwrite a role the reader is in the middle of changing.
  useEffect(() => {
    if (!open || !lens || !detail.data || hydratedLensId.current === lens.id) {
      return;
    }
    const detailedLens: Lens = {
      ...detail.data.lens,
      access: { ...lens.access, ...detail.data.access },
      audience: detail.data.sharing?.audience ?? detail.data.lens.audience,
      home: detail.data.sharing?.home ?? detail.data.lens.home,
      recipientCount: detail.data.sharing?.recipientCount ?? detail.data.lens.recipientCount,
    };
    hydratedLensId.current = lens.id;
    setActiveLens(detailedLens);
    setDraft(recipients);
    setConfirmedRecipients(recipients);
    setMoveTo(detailedLens.home?.kind === 'space' ? detailedLens.home.spaceId : '');
    const serverLinkEnabled = Boolean(detail.data.sharing?.linkAccess?.enabled);
    setLinkAccessEnabled(serverLinkEnabled);
    setConfirmedLinkAccessEnabled(serverLinkEnabled);
  }, [detail.data, lens, open, recipients]);

  const sharedView = includeView ? currentView : undefined;
  const viewState = documentState && Object.keys(documentState).length ? documentState : undefined;
  const shareUrl = currentLens
    ? lensShareUrl(window.location.origin, currentLens.id, sharedView, includeView ? viewState : undefined)
    : '';

  if (!currentLens) {
    return null;
  }

  const mergeActiveLens = (next: Lens) => {
    setActiveLens((current) => {
      const previousSpaceId = current?.home?.kind === 'space' ? current.home.spaceId : null;
      const nextSpaceId = next.spaceId === undefined ? previousSpaceId : next.spaceId;
      const team = spaces.find((space) => space.id === nextSpaceId);
      const home =
        nextSpaceId === previousSpaceId && current?.home
          ? current.home
          : nextSpaceId
            ? { kind: 'space' as const, spaceId: nextSpaceId, spaceName: team?.name ?? 'Team' }
            : { kind: 'personal' as const, ownerEmail: next.ownerEmail };
      return {
        ...current,
        ...next,
        home,
        audience: nextSpaceId
          ? 'team'
          : (next.audience ?? (nextSpaceId === previousSpaceId ? current?.audience : 'private')),
        access: current?.access ?? lens?.access,
      };
    });
  };

  const draftWithInvitee = (): Array<LensShareRecipient> | null => {
    const email = invitee.trim().toLowerCase();
    if (!EMAIL_PATTERN.test(email)) {
      setInviteError('Enter a work email address');
      return null;
    }
    if (!currentSpaceId && email === currentLens.ownerEmail.trim().toLowerCase()) {
      setInviteError('This person already manages the Lens');
      return null;
    }
    if (draft.some((person) => person.email === email)) {
      setInviteError('This person is already on the list');
      return null;
    }
    if (shareLimitReached) {
      setInviteError('Move this Lens into a team or ask Data to certify it before adding more people.');
      return null;
    }
    return [...draft, { email, role: inviteeRole }];
  };

  const addRecipient = async () => {
    const next = draftWithInvitee();
    if (!next) {
      return;
    }
    const person = next[next.length - 1];
    if (await grantAccess(person.email, person.role)) {
      setInvitee('');
      setInviteError(null);
    }
  };

  const grantAccess = async (email: string, role: LensRole = 'viewer') => {
    setSharingFeedback(null);
    try {
      const result = await updateSharing.mutateAsync({
        lensId: currentLens.id,
        grants: [{ email, role }],
      });
      const unsaved = draft.filter(
        (person) =>
          person.email !== email &&
          !confirmedRecipients.some((saved) => saved.email === person.email && saved.role === person.role),
      );
      const removed = confirmedRecipients.filter(
        (saved) => saved.email !== email && !draft.some((person) => person.email === saved.email),
      );
      setConfirmedRecipients(result.recipients);
      setDraft([
        ...result.recipients.filter(
          (saved) =>
            !unsaved.some((person) => person.email === saved.email) &&
            !removed.some((person) => person.email === saved.email),
        ),
        ...unsaved,
      ]);
      mergeActiveLens(result.lens);
      setSharingFeedback({
        message:
          detail.data?.publication?.publishedVersion === null
            ? `${email} was added. Publish this Lens so they can open it.`
            : `${email} can now open this Lens`,
        severity: 'success',
      });
      return true;
    } catch (error) {
      setSharingFeedback({
        message: error instanceof Error ? error.message : 'We couldn’t add this person. Try again.',
        severity: 'error',
      });
      return false;
    }
  };

  const removeRecipient = async (email: string) => {
    setSharingFeedback(null);
    try {
      const result = await updateSharing.mutateAsync({
        lensId: currentLens.id,
        recipients: confirmedRecipients
          .filter((person) => person.email !== email)
          .map(({ email, role }) => ({ email, role })),
      });
      setDraft((current) => current.filter((person) => person.email !== email));
      setConfirmedRecipients(result.recipients);
      mergeActiveLens(result.lens);
      setSharingFeedback({ message: `${email} can no longer open this Lens`, severity: 'success' });
    } catch (error) {
      setSharingFeedback({
        message: error instanceof Error ? error.message : 'We couldn’t remove this person. Try again.',
        severity: 'error',
      });
      return;
    }
  };

  const saveChanges = async () => {
    const result = await updateSharing.mutateAsync({
      lensId: currentLens.id,
      ...(recipientsChanged
        ? canManageSharing
          ? { recipients: draft.map(({ email, role }) => ({ email, role })) }
          : {
              grants: draft
                .filter(
                  (person) =>
                    !confirmedRecipients.some((saved) => saved.email === person.email && saved.role === person.role),
                )
                .map(({ email, role }) => ({ email, role })),
            }
        : {}),
      ...(homeChanged
        ? { spaceId: moveTo || null, ownerEmail: leavingSpace ? newManager.trim().toLowerCase() : undefined }
        : {}),
      ...(linkChanged ? { linkAccessEnabled } : {}),
    });
    if (linkChanged && result.linkAccess?.enabled !== linkAccessEnabled) {
      throw new Error('The service did not confirm link access. Reload this Lens and try again.');
    }
    if (homeChanged && (result.lens.spaceId ?? null) !== (moveTo || null)) {
      throw new Error('The service did not confirm the new Home. Reload this Lens and try again.');
    }
    setDraft(result.recipients);
    setConfirmedRecipients(result.recipients);
    mergeActiveLens(result.lens);
    setMoveTo(result.lens.spaceId ?? '');
    if (linkChanged) {
      setConfirmedLinkAccessEnabled(linkAccessEnabled);
    }
  };

  const leavingSpace = Boolean(currentSpaceId) && !moveTo;
  const homeChanged = (moveTo || '') !== (currentSpaceId || '');
  const linkChanged = linkAccessEnabled !== confirmedLinkAccessEnabled;
  const recipientsChanged =
    draft.length !== confirmedRecipients.length ||
    draft.some(
      (person) => !confirmedRecipients.some((saved) => saved.email === person.email && saved.role === person.role),
    );
  const selectedTeam = spaces.find((space) => space.id === moveTo);
  const audienceTitle = moveTo
    ? (selectedTeam?.name ?? (home?.kind === 'space' && home.spaceId === moveTo ? home.spaceName : 'Team'))
    : 'Personal Lens';
  const teamAudience = selectedTeam?.audience ?? currentTeam?.audience;
  const companyReadable =
    (!detail.data?.publication && certified) || (detail.data?.sharing?.audience ?? currentLens.audience) === 'company';
  const companyShared = Boolean(detail.data?.lens.companyShared ?? currentLens.companyShared);
  const audienceDetail = companyReadable
    ? 'Anyone at LE can view'
    : moveTo
      ? teamAudience === 'everyone'
        ? 'Anyone at LE can view'
        : teamAudience === 'members'
          ? confirmedLinkAccessEnabled
            ? 'Members and anyone at LE with the link can view'
            : 'Team members can view'
          : confirmedLinkAccessEnabled
            ? 'Anyone at LE with the link can view'
            : 'Team visibility controls access'
      : confirmedLinkAccessEnabled
        ? 'Anyone signed in at LE with its link can view.'
        : 'Only people with access can view.';
  const prepareCopy = async (): Promise<LensResponse> => {
    setCopyError(null);
    setCopied(false);
    if (invitee.trim()) {
      throw new Error('Add the person above or clear their email before continuing.');
    }
    if (!detail.data || detail.isError) {
      throw new Error('Reload sharing details before copying this link.');
    }
    if (homeChanged && (!canMoveHome || (leavingSpace && !EMAIL_PATTERN.test(newManager.trim())))) {
      throw new Error('Choose a valid home and personal manager before continuing.');
    }
    if (linkChanged && !canChangeLinkAccess) {
      throw new Error('Only a Manager can change link access.');
    }
    if (homeChanged || recipientsChanged || linkChanged) {
      await saveChanges();
    }
    // Sharing can advance the version. Publish only the revision confirmed after those writes.
    if (detail.data.publication) {
      const fresh = await detail.refetch();
      if (fresh.error || !fresh.data) {
        throw new Error('Could not confirm the current revision. Try again.');
      }
      return fresh.data;
    }
    return detail.data;
  };
  const copyConfirmedLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
    } catch {
      throw new Error(`We couldn’t copy the link. Copy it manually: ${shareUrl}`);
    }
  };
  const copyLink = async () => {
    setCopying(true);
    try {
      await prepareCopy();
      await copyConfirmedLink();
    } catch (error) {
      setCopyError(error instanceof Error ? error.message : 'Could not copy the link. Try again.');
    } finally {
      setCopying(false);
    }
  };
  const publicationState = detail.data?.publication;
  const draftLocked = Boolean(
    publicationState?.editorEmail && publicationState.editorEmail.toLowerCase() !== viewerEmail?.toLowerCase(),
  );
  const publishingNeeded =
    publicationState?.publishedVersion === null ||
    Boolean(publicationState?.hasUnpublishedChanges && detail.data?.access.canEdit && !draftLocked);
  const linkAudience = companyReadable
    ? 'Anyone at LE'
    : linkAccessEnabled
      ? 'Anyone at LE with the link'
      : currentSpaceId
        ? 'Team members and invited people'
        : 'Only invited people';
  const label = (text: string) => (
    <Typography
      sx={{
        color: tokens.accent,
        fontSize: 11,
        fontWeight: 750,
        letterSpacing: '0.09em',
        mb: 1,
        mt: 3,
        textTransform: 'uppercase',
      }}
    >
      {text}
    </Typography>
  );

  return (
    <Dialog
      PaperProps={{
        sx: {
          bgcolor: tokens.canvas,
          borderRadius: '8px',
          m: { xs: 2, sm: 3 },
          maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 48px)' },
          width: 600,
          maxWidth: 'calc(100% - 32px)',
        },
      }}
      onClose={busy ? undefined : onClose}
      open={open}
      maxWidth={false}
      aria-labelledby="lens-share-title"
    >
      <Dialog
        aria-labelledby="withdraw-company-title"
        fullWidth
        maxWidth="xs"
        onClose={busy ? undefined : () => setWithdrawingCompany(false)}
        open={withdrawingCompany}
      >
        <DialogTitle id="withdraw-company-title">Stop sharing with company?</DialogTitle>
        <DialogContent>
          {companyError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {companyError}
            </Alert>
          ) : null}
          <Typography variant="body2">
            People will keep access through existing team membership, direct shares or enabled link access.
            Certification is unchanged.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setWithdrawingCompany(false)}>
            Cancel
          </Button>
          <Button
            disabled={busy}
            onClick={() => {
              setCompanyError(null);
              void updateSharing
                .mutateAsync({ lensId: currentLens.id, companyEnabled: false })
                .then((result) => {
                  mergeActiveLens(result.lens);
                  setWithdrawingCompany(false);
                  setSharingFeedback({
                    message: 'Company sharing removed. Other access is unchanged.',
                    severity: 'success',
                  });
                })
                .catch((error: unknown) =>
                  setCompanyError(
                    error instanceof Error ? error.message : 'Could not update company access. Try again.',
                  ),
                );
            }}
            variant="contained"
          >
            Stop sharing
          </Button>
        </DialogActions>
      </Dialog>
      <Stack direction="row" justifyContent="space-between" sx={{ ...LENS_MODAL_HEADER, flexShrink: 0 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            noWrap
            sx={{
              color: 'inherit',
              opacity: 0.8,
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.13em',
              textTransform: 'uppercase',
            }}
            title={currentLens.title}
          >
            {currentLens.title}
          </Typography>
          <Typography
            component="h2"
            id="lens-share-title"
            sx={{
              color: 'inherit',
              fontFamily: FONT_STACK_SUISSE_INTL,
              fontSize: 18,
              fontWeight: 600,
              lineHeight: 1.3,
            }}
          >
            Share Lens
          </Typography>
        </Box>
        <IconButton aria-label="Close" disabled={busy} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Stack>
      {sharingFeedback && (
        <Alert
          role={sharingFeedback.severity === 'success' ? 'status' : 'alert'}
          severity={sharingFeedback.severity}
          sx={{ borderRadius: 0, flexShrink: 0, px: 3, py: 0.5 }}
        >
          {sharingFeedback.message}
        </Alert>
      )}

      <Box sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', px: 3, pb: 3 }}>
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mt: 2 }}>
          <Box
            aria-hidden="true"
            sx={{
              alignItems: 'center',
              bgcolor: alpha(tokens.accent, 0.07),
              borderRadius: '8px',
              color: tokens.accent,
              display: 'flex',
              flexShrink: 0,
              height: 36,
              justifyContent: 'center',
              width: 36,
            }}
          >
            {moveTo ? (
              <GroupsOutlinedIcon sx={{ fontSize: 19 }} />
            ) : confirmedLinkAccessEnabled ? (
              <LinkOutlinedIcon sx={{ fontSize: 19 }} />
            ) : (
              <LockOutlinedIcon sx={{ fontSize: 19 }} />
            )}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ color: tokens.accent, fontSize: 14, fontWeight: 600 }}>
              Currently in{' '}
              {currentSpaceId ? (currentTeam?.name ?? (home?.kind === 'space' ? home.spaceName : 'Team')) : 'Personal'}
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: 12.5 }}>
              {homeChanged
                ? `This Lens will move to ${audienceTitle}.`
                : publishingNeeded
                  ? 'Publishing won’t move this Lens.'
                  : 'Sharing won’t move this Lens.'}
            </Typography>
            {draft.length > 0 && (
              <Typography color="text.secondary" sx={{ fontSize: 12.5 }}>
                Also shared with {draft.length} {draft.length === 1 ? 'person' : 'people'}.
              </Typography>
            )}
          </Box>
          {canMoveHome && (
            <Button size="small" disabled={busy} onClick={() => setMoreOptions((value) => !value)}>
              Change
            </Button>
          )}
        </Stack>
        {label('Who can open the link')}
        {linkAccessSupported ? (
          <TextField
            select
            fullWidth
            size="small"
            label="Link access"
            value={linkAccessEnabled ? 'company-link' : 'restricted'}
            disabled={busy || !canChangeLinkAccess}
            onChange={(event) => {
              setLinkAccessEnabled(event.target.value === 'company-link');
              setCopied(false);
              setCopyError(null);
            }}
            helperText={
              companyReadable
                ? 'This Lens is already available to everyone at LE.'
                : linkAccessEnabled
                  ? 'Anyone at LE can sign in and view with this link.'
                  : currentSpaceId
                    ? 'Team members and invited people can open this link.'
                    : 'Only invited people can open this link. Copying it won’t give anyone access.'
            }
            FormHelperTextProps={{ sx: { mx: 0, mt: 1, fontSize: 12 } }}
          >
            <MenuItem value="restricted">
              <LockOutlinedIcon sx={{ fontSize: 17, mr: 1 }} />
              Restricted
            </MenuItem>
            <MenuItem value="company-link">
              <PublicOutlinedIcon sx={{ fontSize: 17, mr: 1 }} />
              Anyone at LE with the link
            </MenuItem>
          </TextField>
        ) : (
          <Typography color="text.secondary" sx={{ fontSize: 12.5 }}>
            {personalHome ? 'Current access' : 'Team access'}: {audienceDetail}
          </Typography>
        )}
        {!canShare && (
          <Alert severity="info" sx={{ mt: 2 }}>
            You can copy this link. Only a Manager can change access.
          </Alert>
        )}

        {detail.isError && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            We couldn’t load who this Lens is already shared with, so saving now could remove someone. Close this and
            try again.
          </Alert>
        )}

        {label('Add people')}
        <Typography color="text.secondary" sx={{ fontSize: 12, mb: 1 }}>
          {shareCount} of 10 direct shares
        </Typography>
        {shareLimitReached && (
          <Alert severity="info" sx={{ mb: 1 }}>
            Move this Lens into a team or ask Data to certify it before adding more people.
          </Alert>
        )}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) 112px 64px',
            gap: 1,
            alignItems: 'start',
            '& .MuiOutlinedInput-root': { height: 40, fontSize: 14, lineHeight: '20px' },
            '& .MuiInputLabel-root': { fontSize: 14 },
            '& .MuiOutlinedInput-input': { py: '9px', fontSize: 14, lineHeight: '20px' },
            '& .MuiAutocomplete-root .MuiOutlinedInput-root.MuiInputBase-sizeSmall': { p: 0 },
            '& .MuiAutocomplete-root .MuiOutlinedInput-root.MuiInputBase-sizeSmall .MuiAutocomplete-input': {
              fontSize: 14,
              lineHeight: '20px',
              height: '20px',
              p: '9px 12px',
              boxSizing: 'content-box',
            },
          }}
        >
          <LensRecipientInput
            disabled={!open || !canShare || busy}
            value={invitee}
            error={inviteError}
            excludedEmails={[
              ...draft.map((person) => person.email),
              ...(!currentSpaceId ? [currentLens.ownerEmail] : []),
            ]}
            onChange={(value) => {
              setInvitee(value);
              setInviteError(null);
              setSharingFeedback(null);
            }}
          />
          <TextField
            disabled={!canShare || busy}
            inputProps={{ 'aria-label': 'Access for the person being added' }}
            onChange={(event) => {
              setInviteeRole(event.target.value as LensRole);
              setSharingFeedback(null);
            }}
            select
            size="small"
            sx={{ minWidth: 0 }}
            value={inviteeRole}
          >
            {allowedRecipientRoles.map((role) => (
              <MenuItem key={role.value} value={role.value}>
                {role.label}
              </MenuItem>
            ))}
          </TextField>
          <Button
            disabled={!canShare || busy || !invitee.trim() || shareLimitReached}
            onClick={() => void addRecipient()}
            size="small"
            sx={{ height: 40, minWidth: 64, p: '9px 12px', fontSize: 14, lineHeight: '20px' }}
            variant="outlined"
          >
            Add
          </Button>
        </Box>
        <Stack sx={{ mt: 1.5 }}>
          {draft.map((person) => (
            <Stack
              alignItems="center"
              direction="row"
              key={person.email}
              spacing={1.25}
              sx={{ borderBottom: '1px solid', borderColor: alpha(tokens.accent, 0.12), py: 1.25 }}
            >
              <Avatar
                sx={{
                  bgcolor: alpha(tokens.accent, 0.12),
                  color: tokens.accent,
                  fontSize: 11,
                  height: 28,
                  width: 28,
                }}
              >
                {person.email.charAt(0).toUpperCase()}
              </Avatar>
              <Typography noWrap sx={{ flex: 1, fontSize: 12.5, minWidth: 0 }} title={person.email}>
                {person.email}
              </Typography>
              <TextField
                disabled={!canShare || busy || (!canShareEdit && person.role === 'editor')}
                inputProps={{ 'aria-label': `Access for ${person.email}` }}
                onChange={(event) => {
                  setSharingFeedback(null);
                  setDraft((current) =>
                    current.map((entry) =>
                      entry.email === person.email ? { ...entry, role: event.target.value as LensRole } : entry,
                    ),
                  );
                }}
                select
                size="small"
                sx={{ minWidth: 112, '& .MuiSelect-select': { fontSize: 12 } }}
                value={person.role}
              >
                {RECIPIENT_ROLES.map((role) => (
                  <MenuItem
                    key={role.value}
                    disabled={
                      (!canShareEdit && role.value === 'editor') ||
                      (!canManageSharing && person.role === 'editor' && role.value === 'viewer')
                    }
                    sx={{ fontSize: 12.5 }}
                    value={role.value}
                  >
                    {role.label}
                  </MenuItem>
                ))}
              </TextField>
              <Tooltip title={`Remove ${person.email}`}>
                <span>
                  <IconButton
                    aria-label={`Remove ${person.email}`}
                    disabled={!canManageSharing || busy || !recipientsLoaded}
                    onClick={() => {
                      void removeRecipient(person.email);
                    }}
                    size="small"
                  >
                    <PersonRemoveOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          ))}
        </Stack>
        {publicationState && (
          <Box sx={{ borderTop: '1px solid', borderColor: alpha(tokens.accent, 0.15), pt: 2, mt: 2 }}>
            <Typography sx={{ fontSize: 13 }}>
              {publishingNeeded
                ? `Publishing makes this version available to ${companyReadable ? 'everyone at LE' : linkAccessEnabled ? 'anyone at LE with the link' : currentSpaceId ? 'team members and invited people' : 'people you invite'}.`
                : 'Readers see the published version. Copying its link won’t publish draft changes.'}
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: 12.5, mt: 0.75 }}>
              {publishingDisabled
                ? 'Wait for changes to finish saving before continuing.'
                : draftLocked
                  ? `${publicationState.editorEmail} is editing the draft.`
                  : 'Future edits stay in draft until you publish them.'}
            </Typography>
          </Box>
        )}
        <Button onClick={() => setMoreOptions((current) => !current)} size="small" sx={{ mt: 2 }}>
          {moreOptions ? 'Fewer options' : 'More options'}
        </Button>
        <Box sx={{ display: moreOptions ? 'block' : 'none' }}>
          {companyShared && canManageSharing ? (
            <>
              {label('Company access')}
              <Typography color="text.secondary" variant="body2">
                This report is explicitly shared with the company. Removing this access keeps team membership, direct
                shares and link access as they are.
              </Typography>
              <Button
                disabled={busy}
                onClick={() => {
                  setCompanyError(null);
                  setWithdrawingCompany(true);
                }}
                size="small"
                sx={{ mt: 1 }}
              >
                Stop sharing with company
              </Button>
            </>
          ) : null}
          {label('Home')}
          <Typography color="text.secondary" sx={{ fontSize: 12, mb: 1 }}>
            An open team lets anyone at LE read its Lenses. The Manager controls the team. You keep edit.
          </Typography>
          {spacesLoading && <Typography variant="body2">Loading teams…</Typography>}
          {spacesError && <Alert severity="warning">We couldn’t load teams. Close this panel and try again.</Alert>}
          {writableSpaces.length === 0 && !currentSpaceId ? null : (
            <>
              <TextField
                disabled={busy || !canMoveHome}
                fullWidth
                label="Home"
                onChange={(event) => {
                  setMoveTo(event.target.value);
                  setMoveError(null);
                  setSharingFeedback(null);
                }}
                select
                size="small"
                value={moveTo}
              >
                <MenuItem value="" disabled={certified}>
                  Personal
                </MenuItem>
                {spaces
                  .filter((space) => space.id === currentSpaceId || writableSpaces.includes(space))
                  .map((space) => (
                    <MenuItem key={space.id} value={space.id} disabled={!writableSpaces.includes(space)}>
                      {space.name}
                    </MenuItem>
                  ))}
              </TextField>
              {leavingSpace && (
                <TextField
                  disabled={busy}
                  fullWidth
                  label="New personal manager"
                  onChange={(event) => {
                    setNewManager(event.target.value);
                    setSharingFeedback(null);
                  }}
                  size="small"
                  sx={{ mt: 1.5 }}
                  value={newManager}
                />
              )}
            </>
          )}

          {label('Certification')}
          {currentLens.certification?.badge.kind === 'certified' ? (
            <Typography variant="body2">Certified by Data.</Typography>
          ) : currentLens.certification?.request.kind === 'open' ? (
            <Typography variant="body2">Data has your certification request.</Typography>
          ) : (
            <Stack spacing={1}>
              <Typography color="text.secondary" variant="body2">
                Ask Data to review this Lens for the area pages. It needs a team home first.
              </Typography>
              {certificationError && <Alert severity="error">{certificationError}</Alert>}
              <Button
                disabled={
                  busy || !canShare || !effectiveAccess?.canRequestCertification || !currentSpaceId || homeChanged
                }
                onClick={() => {
                  setCertificationError(null);
                  void certificationAction
                    .mutateAsync({ lensId: currentLens.id, action: 'request' })
                    .then(() =>
                      setSharingFeedback({ message: 'Data has your certification request.', severity: 'success' }),
                    )
                    .catch((error: unknown) =>
                      setCertificationError(error instanceof Error ? error.message : 'Could not ask Data. Try again.'),
                    );
                }}
                size="small"
                variant="outlined"
              >
                Ask Data to certify
              </Button>
              {homeChanged && <Typography variant="caption">Save the team home before asking Data.</Typography>}
            </Stack>
          )}

          {(currentView || viewState) && (
            <FormControlLabel
              sx={{ mt: 2 }}
              control={
                <Checkbox
                  checked={includeView}
                  onChange={(_, checked) => {
                    setIncludeView(checked);
                    setCopied(false);
                  }}
                />
              }
              label="Include current filters, dates and comparison"
            />
          )}
          {open && (
            <LensShareExport
              key={currentLens.id}
              lens={currentLens}
              url={shareUrl}
              view={sharedView}
              disabled={busy || detail.isError}
            />
          )}
        </Box>
      </Box>

      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{
          flexShrink: 0,
          bgcolor: tokens.surface,
          borderTop: '1px solid',
          borderColor: alpha(tokens.accent, 0.16),
          px: 3,
          py: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        {moveError && (
          <Alert severity="error" sx={{ width: '100%' }}>
            {moveError}
          </Alert>
        )}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ maxWidth: 220 }}>
          {linkAccessEnabled || companyReadable ? (
            <PublicOutlinedIcon sx={{ fontSize: 17 }} />
          ) : (
            <LockOutlinedIcon sx={{ fontSize: 17 }} />
          )}
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{linkAudience}</Typography>
            <Typography color="text.secondary" sx={{ fontSize: 11.5 }}>
              Can view
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          {(homeChanged || recipientsChanged || linkChanged) && (
            <Button
              disabled={
                busy ||
                !recipientsLoaded ||
                (homeChanged && (!canMoveHome || (leavingSpace && !EMAIL_PATTERN.test(newManager.trim())))) ||
                (linkChanged && !canChangeLinkAccess)
              }
              onClick={() => {
                if (canShare && (homeChanged || recipientsChanged || linkChanged)) {
                  setMoveError(null);
                  void saveChanges()
                    .then(onClose)
                    .catch((error: unknown) => {
                      setMoveError(error instanceof Error ? error.message : 'We couldn’t save this Lens. Try again.');
                    });
                } else {
                  onClose();
                }
              }}
              variant="text"
            >
              {updateSharing.isPending ? 'Saving…' : 'Save'}
            </Button>
          )}
          {detail.data?.publication ? (
            <LensPublishCopyButton
              detail={detail.data}
              viewerEmail={viewerEmail}
              disabled={busy || detail.isError || !recipientsLoaded || publishingDisabled}
              copied={copied}
              prepare={prepareCopy}
              copy={copyConfirmedLink}
              onPublished={(response) => {
                mergeActiveLens(response.lens);
                setPublished(true);
              }}
              onError={setCopyError}
              onBusy={setCopying}
            />
          ) : (
            <Button
              variant="contained"
              onClick={() => void copyLink()}
              disabled={busy || detail.isError}
              startIcon={<LinkOutlinedIcon />}
            >
              {copied ? 'Copied' : 'Copy link'}
            </Button>
          )}
        </Stack>
      </Stack>
      {copied && (
        <Alert severity="success" role="status" sx={{ borderRadius: 0, px: 3 }}>
          {published ? 'Published. Link copied.' : 'Link copied.'}
        </Alert>
      )}
      {copyError && (
        <Alert severity="error" sx={{ borderRadius: 0, mx: 3, mb: 2 }}>
          {copyError}
        </Alert>
      )}
    </Dialog>
  );
}
